import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, DatePicker, Select, Space } from 'antd';
import ExcelTable from 'components/ui/excel-table';
import {
  ExcelTableColDef,
  ECellValueType,
  ECellValueDisplayMode,
  EExcelTableColumnType,
  AllRowsData,
  RowData,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { ContractAllocationRow, ContractAllocationTableProps } from './types';
import { TaskIcon } from 'assets/icons/task';
import FormActionButtons from 'components/ui/form-action-buttons';
import { validateContractAllocation } from 'components/features/contract-allocation-table/form';
import { FIXED_SETTLEMENT_EXCHANGE_RATE_CURRENCIES } from 'components/features/contract-allocation-table/constants';
import FilterButton from 'components/ui/filter-button';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import { premiumScheduleApi } from 'services/apis';
import usePagination from 'hooks/usePagination';
import { GetPremiumScheduleAvailableForAllocationQueryDtoSettlementCurrencyEnum } from 'libs/pinnacle-sdk';
import { transformContractAllocationRow } from 'components/features/receipt-form/form';
import { format } from 'date-fns';
import { SortedSelect } from 'components/ui/sorted-select';
import { keyBy } from 'lodash';

const { RangePicker } = DatePicker;

const ContractAllocationTable: React.FC<ContractAllocationTableProps> = ({
  allocationContractTableData,
  bankAccountCurrency,
  onContractDataChanged,
  isAllocationDataValid,
  onContractAllocated,
  onAllocationDataValidated,
  sourceBrokerId,
  onAvailableAllocationFetched,
  receiptSettlementExchangeRates,
  form,
}) => {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Watch form values for filtering
  const selectedInsureds = Form.useWatch('insured', form);
  const selectedReinsureds = Form.useWatch('reinsured', form);
  const selectedBinders = Form.useWatch('binder', form);
  const selectedOriginalCurrencies = Form.useWatch('originalCurrency', form);
  const ppwDateRange = Form.useWatch('ppwDateRange', form);

  const { data: options } = useQuery(
    [
      EQueryKeys.OPTIONS_PREMIUM_SCHEDULE_AVAILABLE_FOR_ALLOCATION,
      sourceBrokerId,
      bankAccountCurrency,
    ],
    () => {
      return premiumScheduleApi.getAvailableForAllocationOptions({
        brokerId: sourceBrokerId,
        currency: bankAccountCurrency,
      });
    },
    {
      enabled: !!sourceBrokerId && !!bankAccountCurrency,
      refetchOnWindowFocus: false,
    }
  );

  const insuredOptions = useMemo(() => {
    return options?.insured.map((insured) => ({
      label: insured.name,
      value: insured.insuredId,
    }));
  }, [options]);

  const reinsuredOptions = useMemo(() => {
    return options?.reinsured.map((reinsured) => ({
      label: reinsured.name,
      value: reinsured.reinsuredId,
    }));
  }, [options]);

  const binderOptions = useMemo(() => {
    return options?.binders.map((binder) => ({
      label: binder.binderName,
      value: binder.binderId,
    }));
  }, [options]);

  const originalCurrencyOptions = useMemo(() => {
    return options?.originalCurrencies.map(({ originalCurrency }) => ({
      label: originalCurrency,
      value: originalCurrency,
    }));
  }, [options]);

  const settlementRateByOriginalCurrency = useMemo(
    () => keyBy(receiptSettlementExchangeRates, 'originalCurrency'),
    [receiptSettlementExchangeRates]
  );

  const onDataChange = useCallback(
    async (
      _: RowData<ContractAllocationRow>,
      rows: AllRowsData<ContractAllocationRow>
    ) => {
      return onContractDataChanged?.(
        rows.data.map(
          transformContractAllocationRow(
            bankAccountCurrency,
            settlementRateByOriginalCurrency
          )
        )
      );
    },
    [
      onContractDataChanged,
      bankAccountCurrency,
      settlementRateByOriginalCurrency,
    ]
  );

  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(20);

  useEffect(() => {
    resetPagination();
  }, [
    selectedInsureds,
    selectedReinsureds,
    selectedBinders,
    selectedOriginalCurrencies,
    resetPagination,
  ]);

  const {
    data: { items: contractAvailableForAllocation, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading: isLoadingContractAvailableForAllocation,
  } = useQuery(
    [
      EQueryKeys.CONTRACT_AVAILABLE_FOR_ALLOCATION,
      sourceBrokerId,
      pageSize,
      currentPage,
      bankAccountCurrency,
      selectedInsureds,
      selectedReinsureds,
      selectedBinders,
      selectedOriginalCurrencies,
      ppwDateRange,
    ],
    () => {
      if (!sourceBrokerId || !bankAccountCurrency) {
        return undefined;
      }
      return premiumScheduleApi.getPremiumScheduleAvailableForAllocation({
        getPremiumScheduleAvailableForAllocationQueryDto: {
          brokerId: sourceBrokerId,
          settlementCurrency:
            bankAccountCurrency as GetPremiumScheduleAvailableForAllocationQueryDtoSettlementCurrencyEnum,
          page: currentPage,
          pageSize,
          insuredIds: selectedInsureds,
          reinsuredIds: selectedReinsureds,
          binderIds: selectedBinders,
          originalCurrencies: selectedOriginalCurrencies,
          ppwDueDateFrom: ppwDateRange?.[0]?.startOf('day').toDate(),
          ppwDueDateTo: ppwDateRange?.[1]?.endOf('day').toDate(),
        },
      });
    },
    {
      enabled: !!sourceBrokerId && !!bankAccountCurrency,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(
    () => onAvailableAllocationFetched(contractAvailableForAllocation || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractAvailableForAllocation]
  );

  // Column definitions
  const columnDefs = useMemo(
    (): ExcelTableColDef<ContractAllocationRow>[] => [
      {
        headerName: '',
        headerComponent: () => (
          <div className="flex items-center justify-center w-full h-full">
            <TaskIcon />
          </div>
        ),
        field: 'isSelected',
        width: 100,
        columnType: EExcelTableColumnType.CHECKBOX,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      {
        headerName: t('contractAllocation.documentNo'),
        field: 'documentNo',
        width: 200,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.insured'),
        field: 'insured',
        width: 150,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.reinsured'),
        field: 'reinsured',
        width: 150,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.binder'),
        field: 'binder',
        width: 150,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('premiumDue.closing'),
        field: 'closing',
        width: 100,
        editable: false,
        resizable: false,
        columnType: EExcelTableColumnType.CHECKBOX,
      },
      {
        headerName: t('premiumProcessing.closingReference'),
        field: 'closingReference',
        width: 150,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.originalCurrency'),
        field: 'originalCurrency',
        width: 120,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.bookedExchangeRate'),
        field: 'bookedExchangeRate',
        width: 140,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 5,
        editable: false,
        resizable: true,
        hide: true,
      },
      {
        headerName: t('contractDetail.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        width: 180,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        editable: false,
        resizable: true,
        hide: true,
      },
      {
        headerName: t('contractAllocation.originalNetAmountDueBroker'),
        field: 'originalNetAmountDueBroker',
        width: 180,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.paidAmount'),
        field: 'paidAmount',
        width: 120,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.settlementCurrency'),
        field: 'settlementCurrency',
        width: 140,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.settlementExchangeRate'),
        field: 'settlementExchangeRate',
        width: 160,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 5,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.forexGainLoss'),
        field: 'forexGainLoss',
        width: 140,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 2,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAllocation.forexPercentage'),
        field: 'forexPercentage',
        width: 120,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        precision: 2,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('premiumProcessing.ppwDueDate'),
        field: 'ppwDueDate',
        minWidth: 150,
        resizable: true,
        cellRenderer: (params: { value: string | Date }) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
    ],
    [t, bankAccountCurrency]
  );

  const selectedRows = useMemo(() => {
    return allocationContractTableData.filter((row) => row.isSelected);
  }, [allocationContractTableData]);

  const onErrorChange = useCallback(
    (isError: { strict: boolean; soft: boolean }) =>
      onAllocationDataValidated(!isError.strict),
    [onAllocationDataValidated]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full">
        <div className="flex items-center justify-end">
          <FilterButton
            isActive={isFilterOpen}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          />
        </div>

        {isFilterOpen && (
          <div className="my-4 p-4 border rounded-lg bg-gray-50">
            <Space direction="horizontal" wrap className="w-full">
              <Form.Item name="insured" className="min-w-[200px] mb-2 h-8">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('insured.selectInsured')}
                  options={insuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="reinsured" className="min-w-[200px] mb-2 h-8">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('premiumDue.selectReinsured')}
                  options={reinsuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="binder" className="min-w-[200px] mb-2 h-8">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('binder.selectBinder')}
                  options={binderOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="originalCurrency"
                className="min-w-[250px] mb-2 h-8"
              >
                <SortedSelect
                  mode="multiple"
                  placeholder={t('general.selectField', {
                    field: t('contractAllocation.originalCurrency'),
                  })}
                  options={originalCurrencyOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="ppwDateRange" className="w-[280px] mb-2 h-8">
                <RangePicker
                  format="DD/MM/YYYY"
                  size="large"
                  placeholder={[
                    t('premiumDue.ppwDateFrom'),
                    t('premiumDue.ppwDateTo'),
                  ]}
                  allowClear
                  className="w-full"
                />
              </Form.Item>
            </Space>
          </div>
        )}
      </div>

      <ExcelTable
        onErrorChange={onErrorChange}
        tableContainerClassName={`h-[${
          Math.max(Math.min(allocationContractTableData.length, 20), 10) * 3
        }rem]`}
        data={allocationContractTableData.map(
          transformContractAllocationRow(
            bankAccountCurrency,
            settlementRateByOriginalCurrency
          )
        )}
        columnDefs={columnDefs}
        onDataChange={onDataChange}
        validateRow={validateContractAllocation}
        rowKeyColName="id"
        getRowClass={(params) => {
          if (params.data && params.data.isSelected) {
            return 'excel-table-row-selected';
          }
          return '';
        }}
        enableErrorBorder
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={total}
        loading={isLoadingContractAvailableForAllocation}
      />
      <FormActionButtons
        isConfirmDisabled={selectedRows.length === 0 || !isAllocationDataValid}
        includeCancel={false}
        onConfirm={() => onContractAllocated(selectedRows)}
        confirmOutlined={false}
        confirmText={t('contractAllocation.allocate')}
        isFormSubmit={false}
      />
    </div>
  );
};

export default ContractAllocationTable;
